import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { Tc } from 'src/app/components/core/_infrastructure/types/common';
import { EnumTwTcResultadoInspeccionCam, ResultadoInspeccionSelected, TwInspeccionUiCamViewModel } from '../../inspeccion.types';
import { InspeccionPeriodoCamViewModel } from 'src/app/components/_mantenimiento/campanias/periodos/inspecciones/inspecciones.types';

@Component({
    selector: 'app-inspeccion-ui-edit',
    templateUrl: './inspeccion-ui-edit.component.html',
    styleUrls: ['./inspeccion-ui-edit.component.scss'],
})
export class InspeccionUiEditComponent extends ComponentBase implements OnChanges, OnInit {
    @Input() readonly: boolean;
    @Input() inspeccion: TwInspeccionUiCamViewModel;
    @Input() listaInspeccionPeriodo: InspeccionPeriodoCamViewModel[];
    @Input() resultadosInspeccion: Tc[];
    @Input() formGroup: FormGroup;
    @Input() IdTipoSolicitudCam: number;
    @Output() tipoInspeccionSelected = new EventEmitter<number>();
    @Output() resultadoSelected = new EventEmitter<ResultadoInspeccionSelected>();

    isCollapsedCabecera = false;
    tiposInspeccion: Tc[];
    requiredFechaCaducidad = false;
    caducidadMinimumDate = new Date(); // La fecha mínima que aparecerá en el input calendario para la indicar la fecha de caducidad.

    constructor(
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.updateForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.inspeccion?.currentValue) {
            this.inspeccion = { ...changes.inspeccion.currentValue };
            this.updateForm();
            if (this.listaInspeccionPeriodo) {
                this.setDependentOnTipoInspeccion(this.inspeccion?.IdPeriodoInspeccionCam);
            }
        }
        if (changes?.listaInspeccionPeriodo?.currentValue) {
            this.populateListTiposInspeccion();
            if (this.inspeccion) {
                this.setDependentOnTipoInspeccion(this.inspeccion?.IdPeriodoInspeccionCam);
            }
        }
    }

    /**
     * Se construye la lista que será utilizada en el desplegable.
     */
    private populateListTiposInspeccion(): void {
        this.tiposInspeccion = [];
        for (let i = 0; i < this.listaInspeccionPeriodo?.length; i++) {
            const tipoInspeccion = new Tc();
            tipoInspeccion.Id = this.listaInspeccionPeriodo[i].Id;
            tipoInspeccion.Descripcion = this.listaInspeccionPeriodo[i].TipoInspeccion;

            this.tiposInspeccion.push(tipoInspeccion);
        }
    }

    /**
     * This functions sets the values in the inspeccion (@Input) into the form.
     */
    private updateForm(): void {
        this.formGroup.setValue({
            IdPeriodoInspeccionCam: this.inspeccion?.IdPeriodoInspeccionCam || null,
            NombreInspeccion: '', // Se obtiene a posteriori a partir del tipo de inspección seleccionado para la inspección
            FechaCaducidad: this.inspeccion?.FechaCaducidad ? new Date(this.inspeccion?.FechaCaducidad) : '',
            Observaciones: this.inspeccion?.Observaciones || '',
            FechaInspeccion: this.inspeccion?.FechaInspeccion ? new Date(this.inspeccion?.FechaInspeccion) : '',
            Inspector: this.inspeccion?.Inspector || '',
            // Not visible but needed
            Id: this.inspeccion?.Id || null,
            IdUnidadInspeccionCam: this.inspeccion?.IdUnidadInspeccionCam || null,
            IdUsuario: this.inspeccion?.IdUsuario || null,
            IdResultadoInspeccionCam: this.inspeccion?.IdResultadoInspeccionCam || null,
            IdUsuarioModificacion: this.inspeccion?.IdUsuarioModificacion || null,
            FechaModificacion: this.inspeccion?.FechaModificacion ? new Date(this.inspeccion?.FechaModificacion) : '',
            IdUsuarioBorrado: this.inspeccion?.IdUsuarioBorrado || null,
            FechaBorrado: this.inspeccion?.FechaBorrado ? new Date(this.inspeccion?.FechaBorrado) : '',
        });

        this.form = this.formGroup; // componentBase uses this.form
    }

    /**
     * Manejador que permite emitir el evento que indica que ha cambiado el tipo de inspección.
     * @param idTipoInspeccion number
     */
    onSelectTipoInspeccion(idTipoInspeccion: number): void {
        this.tipoInspeccionSelected.emit(idTipoInspeccion);
        this.setDependentOnTipoInspeccion(idTipoInspeccion);
    }

    /**
     * Manejador que permite emitir el evento que indica que ha cambiado el resultado de la inspección.
     * @param idResultado number
     */
    onSelectResultado(idResultado: number, idPeriodoInspeccionCam: number): void {
        const resultadoInspeccionSelected = new ResultadoInspeccionSelected();
        resultadoInspeccionSelected.idResultado = idResultado;
        resultadoInspeccionSelected.idPeriodoInspeccionCam = idPeriodoInspeccionCam;

        if (idResultado && idResultado === EnumTwTcResultadoInspeccionCam.ID_RECHAZADA) {
            this.form.patchValue({
                FechaCaducidad: '',
            });
            this.form.controls['FechaCaducidad'].disable();
            if (this.requiredFechaCaducidad) {
                this.removeValidators('FechaCaducidad');
                this.requiredFechaCaducidad = false;
            }
        } else {
            this.form.controls['FechaCaducidad'].enable();
            if (this.requiredFechaCaducidad) {
                this.addValidator('FechaCaducidad', Validators.required);
            }
        }
        this.resultadoSelected.emit(resultadoInspeccionSelected);
    }

    private setDependentOnTipoInspeccion(idTipoInspeccion: number): void {
        const tipoInspeccionSeleccionado = this.listaInspeccionPeriodo.find(s => s.Id === idTipoInspeccion);

        if (tipoInspeccionSeleccionado) {
            this.form.patchValue({
                NombreInspeccion: tipoInspeccionSeleccionado?.Nombre || '',
            });
            if (!tipoInspeccionSeleccionado.TieneFechaFin && !this.readonly) {
                this.form.patchValue({
                    FechaCaducidad: null,
                });
            }
            this.requiredFechaCaducidad = tipoInspeccionSeleccionado.TieneFechaFin;
        } else {
            if (!this.readonly) {
                this.form.patchValue({
                    FechaCaducidad: null,
                });
            }
            this.requiredFechaCaducidad = false;
        }

        if (this.requiredFechaCaducidad) {
            this.addValidator('FechaCaducidad', Validators.required);
        } else {
            this.removeValidators('FechaCaducidad');
        }
    }

    get title(): string {
        return this.inspeccion?.Id !== 0 ? (this.readonly ? 'Detalle de la' : 'Editar') : 'Nueva'; // work mode: readonly, edition or creation
    }
}
